<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { CatalogueItemGroup } from '@/models/Catalogue';
import gettext from '@/i18n/gettext';
import {
  getAllCatalogueGroupsFlat,
  getCatalogueGroupPath,
  getCatalogueGroupLevel,
} from '@/composables/catalogue/useCatalogueGroupHierarchy';

const { $gettext } = gettext;

const props = defineProps<{
  groups: CatalogueItemGroup[];
  allGroups?: CatalogueItemGroup[];
  expandAll?: boolean;
}>();

const emit = defineEmits<{
  (e: 'edit', group: CatalogueItemGroup): void;
  (e: 'delete', group: CatalogueItemGroup): void;
}>();

const expandedGroups = ref<Set<string>>(new Set());
const itemsPerPage = ref(10);
const currentPage = ref(1);

const handleEdit = (group: CatalogueItemGroup) => {
  emit('edit', group);
};

const handleDelete = (group: CatalogueItemGroup) => {
  emit('delete', group);
};

const toggleExpanded = (groupUuid: string) => {
  if (expandedGroups.value.has(groupUuid)) {
    expandedGroups.value.delete(groupUuid);
  } else {
    expandedGroups.value.add(groupUuid);
  }
};

const isExpanded = (groupUuid: string) => {
  return expandedGroups.value.has(groupUuid);
};

const hasChildren = (group: CatalogueItemGroup) => {
  return group.children && group.children.length > 0;
};

const flattenedGroups = computed(() => {
  const result: Array<
    CatalogueItemGroup & {
      level: number;
      path: string;
      isVisible: boolean;
      hierarchyDepth: number;
    }
  > = [];

  // Get all groups as a flat array for hierarchy calculations
  const allGroups = props.allGroups || getAllCatalogueGroupsFlat(props.groups);

  // Only show root groups (parent groups) by default
  const rootGroups = props.groups.filter((group) => !group.parentUuid);

  rootGroups.forEach((group) => {
    const path = getCatalogueGroupPath(group, allGroups);
    const hierarchyDepth = getCatalogueGroupLevel(group, allGroups);

    result.push({
      ...group,
      level: 0,
      path,
      isVisible: true,
      hierarchyDepth,
    });

    // Add children only if expanded
    if (hasChildren(group) && isExpanded(group.uuid)) {
      const addChildren = (children: CatalogueItemGroup[], level: number) => {
        children.forEach((child) => {
          const childPath = getCatalogueGroupPath(child, allGroups);
          const childHierarchyDepth = getCatalogueGroupLevel(child, allGroups);

          result.push({
            ...child,
            level,
            path: childPath,
            isVisible: true,
            hierarchyDepth: childHierarchyDepth,
          });

          // Recursively add grandchildren if expanded (no depth limit)
          if (hasChildren(child) && isExpanded(child.uuid)) {
            addChildren(child.children, level + 1);
          }
        });
      };

      addChildren(group.children, 1);
    }
  });

  return result;
});

const expandAllNodes = () => {
  const addAll = (list: CatalogueItemGroup[]) => {
    list.forEach((g) => {
      expandedGroups.value.add(g.uuid);
      if (g.children && g.children.length > 0) {
        addAll(g.children);
      }
    });
  };
  addAll(props.groups);
};

watch(
  () => props.expandAll,
  (val) => {
    if (val) {
      expandAllNodes();
    }
  },
);

// Available items per page options
const itemsPerPageOptions = [10, 25, 50, 100, 125, 150];

// Function to automatically adjust items per page based on expanded items
const adjustItemsPerPage = () => {
  const totalVisibleItems = flattenedGroups.value.length;
  const currentItemsPerPage = itemsPerPage.value;

  // Find the next available option that can accommodate all visible items
  const nextOption = itemsPerPageOptions.find(
    (option) => option >= totalVisibleItems,
  );

  if (nextOption && nextOption > currentItemsPerPage) {
    itemsPerPage.value = nextOption;
  }
};

// Watch for changes in flattened groups and adjust pagination
watch(
  flattenedGroups,
  () => {
    adjustItemsPerPage();
  },
  { deep: true },
);

const headers = [
  {
    title: $gettext('Group Name'),
    align: 'start' as const,
    sortable: true,
    key: 'name',
  },
  {
    title: $gettext('Hierarchy Path'),
    align: 'start' as const,
    sortable: false,
    key: 'hierarchy',
  },
  {
    title: $gettext('Actions'),
    key: 'actions',
    sortable: false,
    align: 'end' as const,
  },
];
</script>

<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="flattenedGroups"
      :items-per-page="itemsPerPage"
      :items-per-page-options="itemsPerPageOptions"
      item-value="uuid"
      @update:options="
        (options) => {
          if (options.itemsPerPage) itemsPerPage = options.itemsPerPage;
          if (options.page) currentPage = options.page;
        }
      "
    >
      <template v-slot:[`item.name`]="{ item }">
        <div class="d-flex align-center">
          <div
            v-if="hasChildren(item)"
            class="mr-2 cursor-pointer"
            @click.stop="toggleExpanded(item.uuid)"
          >
            <v-icon
              :icon="
                isExpanded(item.uuid) ? 'mdi-chevron-down' : 'mdi-chevron-right'
              "
              size="small"
            />
          </div>
          <div v-else class="mr-2" style="width: 20px"></div>

          <div
            class="d-flex align-center"
            :style="{ marginLeft: `${item.level * 20}px` }"
          >
            <span>{{ item.name }}</span>
          </div>
        </div>
      </template>

      <template v-slot:[`item.hierarchy`]="{ item }">
        <div class="d-flex align-center">
          <v-chip
            v-if="item.hierarchyDepth > 0"
            size="x-small"
            variant="outlined"
            color="info"
            class="mr-2"
          >
            {{ $gettext('Level') }} {{ item.hierarchyDepth }}
          </v-chip>
          <span class="text-caption text-grey">{{ item.path }}</span>
        </div>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-icon
          icon="mdi-pencil"
          size="small"
          class="mr-2"
          @click.stop="handleEdit(item)"
        />
        <v-icon
          icon="mdi-delete"
          size="small"
          @click.stop="handleDelete(item)"
        />
      </template>
    </v-data-table>
  </div>
</template>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}

.v-data-table {
  cursor: pointer;
}

.hierarchy-item {
  transition: all 0.8s ease;

  &:hover {
    background-color: rgba(var(--v-theme-primary), 0.1);
  }
}

.hierarchy-depth-indicator {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -10px;
    top: 50%;
    width: 2px;
    height: 20px;
    background-color: var(--v-theme-primary);
    transform: translateY(-50%);
  }
}
</style>
